import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeSelectorContainer, ThemeButton, ThemeMenu } from './styles';
import { useThemeStore } from '../../store/themeStore';
import { applyTheme } from '../../utils/terminalUtils';
import { BsTerminal } from 'react-icons/bs';
import { FaUbuntu } from 'react-icons/fa';
import { VscTerminalPowershell } from 'react-icons/vsc';

const ThemeSelector: React.FC = () => {
  const { t } = useTranslation();
  const { theme, setTheme } = useThemeStore();
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  
  // Menüyü açıp kapatma
  const toggleMenu = () => {
    setIsOpen(prev => !prev);
  };

  // Tema değiştirme
  const handleThemeChange = (newTheme: 'cmd' | 'powershell' | 'ubuntu') => {
    setTheme(newTheme);
    applyTheme(newTheme);
    setIsOpen(false); // Tema seçildiğinde menüyü kapat
  };
  
  // Menü dışı tıklama kontrolü
  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (!target.closest('.theme-selector')) {
      setIsOpen(false);
    }
  };

  // Otomatik kapanma zamanı ve dış tıklama olayı
  useEffect(() => {
    // Menü açıksa 5 saniye sonra otomatik kapat
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      timeoutRef.current = window.setTimeout(() => {
        setIsOpen(false);
      }, 5000);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isOpen]);

  // Şu anki temaya göre ikon al
  const getCurrentThemeIcon = () => {
    switch(theme) {
      case 'cmd':
        return BsTerminal({ size: 20 });
      case 'powershell':
        return VscTerminalPowershell({ size: 20 });
      case 'ubuntu':
        return FaUbuntu({ size: 20, color: "#E95420" });
      default:
        return BsTerminal({ size: 20 });
    }
  };
  
  return (
    <ThemeSelectorContainer className="theme-selector">
      {/* Ana buton - mevcut tema */}
      <ThemeButton 
        $theme={theme}
        $isSelected={true}
        onClick={toggleMenu}
        title={t(`theme.${theme}`)}
        aria-label={t(`theme.${theme}`)}
        $isMainButton={true}
      >
        {getCurrentThemeIcon()}
      </ThemeButton>
      
      {/* Tema menüsü */}
      <ThemeMenu $isOpen={isOpen}>
        {/* CMD butonu */}
        {theme !== 'cmd' && (
          <ThemeButton 
            $theme="cmd" 
            $isSelected={false} 
            onClick={() => handleThemeChange('cmd')}
            title={t('theme.cmd')}
            aria-label={t('theme.cmd')}
            $isMainButton={false}
          >
            {BsTerminal({ size: 20 })}
          </ThemeButton>
        )}
        
        {/* PowerShell butonu */}
        {theme !== 'powershell' && (
          <ThemeButton 
            $theme="powershell" 
            $isSelected={false} 
            onClick={() => handleThemeChange('powershell')}
            title={t('theme.powershell')}
            aria-label={t('theme.powershell')}
            $isMainButton={false}
          >
            {VscTerminalPowershell({ size: 20 })}
          </ThemeButton>
        )}
        
        {/* Ubuntu butonu */}
        {theme !== 'ubuntu' && (
          <ThemeButton 
            $theme="ubuntu" 
            $isSelected={false} 
            onClick={() => handleThemeChange('ubuntu')}
            title={t('theme.ubuntu')}
            aria-label={t('theme.ubuntu')}
            $isMainButton={false}
          >
            {FaUbuntu({ size: 20, color: "#E95420" })}
          </ThemeButton>
        )}
      </ThemeMenu>
    </ThemeSelectorContainer>
  );
};

export default ThemeSelector; 